/**
 * Admin Roles service, backed by Firestore. Writes are admin-only via
 * Firestore rules (verified admin-domain email).
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
} from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';
import { db } from '../../../../shared/firebase';

const now = () => new Date().toISOString();

const withFrameworkShape = (resp) => ({
  ...resp,
  frameworks: Array.isArray(resp.frameworks) ? resp.frameworks : [],
  frameworkCount: Array.isArray(resp.frameworks) ? resp.frameworks.length : 0,
});

async function fetchResponsibilitiesForRole(roleId) {
  const snap = await getDocs(query(collection(db, 'responsibilities'), where('roleId', '==', roleId)));
  const results = [];
  snap.forEach((d) => results.push(withFrameworkShape(d.data())));
  return results;
}

/**
 * Get all roles with their responsibilities and frameworks
 * @returns {Promise<Array>}
 */
export const getAllRoles = async () => {
  try {
    const rolesSnap = await getDocs(collection(db, 'roles'));
    const roles = [];
    rolesSnap.forEach((d) => roles.push(d.data()));
    return Promise.all(
      roles.map(async (role) => {
        const responsibilities = await fetchResponsibilitiesForRole(role.roleId);
        return {
          ...role,
          responsibilities,
          responsibilityCount: responsibilities.length,
        };
      })
    );
  } catch (error) {
    console.error('Error fetching roles:', error);
    throw error;
  }
};

/**
 * Get role by ID with details
 * @returns {Promise<Object>}
 */
export const getRoleById = async (roleId) => {
  try {
    const snap = await getDoc(doc(db, 'roles', roleId));
    if (!snap.exists()) {
      throw new Error('Role not found');
    }
    const responsibilities = await fetchResponsibilitiesForRole(roleId);
    return {
      ...snap.data(),
      responsibilities,
      responsibilityCount: responsibilities.length,
    };
  } catch (error) {
    console.error('Error fetching role:', error);
    throw error;
  }
};

/**
 * Create new role
 * @returns {Promise<Object>}
 */
export const createRole = async (data) => {
  try {
    const roleId = uuidv4();
    const role = {
      roleId,
      roleName: data.roleName,
      description: data.description || null,
      orgId: data.orgId || null,
      createdAt: now(),
      updatedAt: now(),
    };
    await setDoc(doc(db, 'roles', roleId), role);
    return role;
  } catch (error) {
    console.error('Error creating role:', error);
    throw error;
  }
};

/**
 * Update role
 * @returns {Promise<Object>}
 */
export const updateRole = async (roleId, updates) => {
  try {
    const ref = doc(db, 'roles', roleId);
    const fields = { updatedAt: now() };
    if (updates.roleName !== undefined) fields.roleName = updates.roleName;
    if (updates.description !== undefined) fields.description = updates.description;
    if (updates.orgId !== undefined) fields.orgId = updates.orgId;
    await updateDoc(ref, fields);
    const snap = await getDoc(ref);
    return snap.data();
  } catch (error) {
    console.error('Error updating role:', error);
    throw error;
  }
};

/**
 * Delete role (detaches its responsibilities rather than deleting them)
 * @returns {Promise<Object>}
 */
export const deleteRole = async (roleId) => {
  try {
    const responsibilities = await fetchResponsibilitiesForRole(roleId);
    await Promise.all(
      responsibilities.map((resp) =>
        updateDoc(doc(db, 'responsibilities', resp.responsibilityId), { roleId: null })
      )
    );
    await deleteDoc(doc(db, 'roles', roleId));
    return { success: true, message: 'Role deleted successfully' };
  } catch (error) {
    console.error('Error deleting role:', error);
    throw error;
  }
};
